import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { useAuth } from '../../context/AuthContext';
import { getActiveSites } from '../../services/siteService';
import { getAccountByUnit, getUnitByUnitId } from '../../services/userService';
import {
  getConnectionFeeCharges,
  writeOffConnectionFeeCharge,
} from '../../services/connectionFeeService';
import {
  canAccessMonthlyFixedChargeWriteOff,
  canWriteOffCharge,
  centsToRands,
  formatBillingPeriod,
  formatFixedChargeDate,
  getResolvedAccountUuid,
  getResolvedUnitUuid,
  getUserEmail,
} from '../../utils/monthlyFixedChargeAdmin';

const STATUS_STYLES = {
  PENDING: 'bg-amber-100 text-amber-800',
  PARTIAL: 'bg-blue-100 text-blue-800',
  SETTLED: 'bg-green-100 text-green-800',
  WRITTEN_OFF: 'bg-gray-200 text-gray-600',
};

const STATUS_LABELS = {
  PENDING: 'Pending',
  PARTIAL: 'Partial',
  SETTLED: 'Settled',
  WRITTEN_OFF: 'Written Off',
};

const getChargeErrorMessage = (error) => {
  const status = error?.response?.status;
  const message = error?.response?.data?.message || error?.message || '';

  if (status === 400 && message.toLowerCase().includes('chargeuuid')) {
    return 'Please select a monthly fixed charge to write off.';
  }

  if (status === 400 && message.toLowerCase().includes('reason')) {
    return 'Please provide a reason for the write-off.';
  }

  if (status === 404) {
    return 'The selected monthly fixed charge could not be found.';
  }

  if (status === 409) {
    return 'Only pending or partially paid monthly fixed charges can be written off.';
  }

  if (status === 401 || status === 403) {
    return 'You do not have permission to write off monthly fixed charges.';
  }

  return message || 'Something went wrong. Please try again.';
};

export const MonthlyFixedChargeAccessDenied = () => (
  <div className="container mx-auto px-4 py-8">
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded" role="alert">
      You do not have permission to write off monthly fixed charges.
    </div>
  </div>
);

export const MonthlyFixedChargeTable = ({ charges = [], onWriteOff = undefined }) => {
  if (!charges.length) {
    return (
      <div className="bg-white shadow-md rounded p-6 mt-6">
        <p className="text-gray-500">No monthly fixed charge records found for this account.</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded p-6 mt-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Monthly Fixed Charges</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billing period</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount due</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid/debited</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outstanding</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created at</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Settled at</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {charges.map((charge) => {
              const writable = canWriteOffCharge(charge);

              return (
                <tr key={charge.chargeUuid}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {formatBillingPeriod(charge.billingPeriod)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {centsToRands(charge.amountDueCents)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {centsToRands(charge.amountDebitedCents)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {centsToRands(charge.amountOutstandingCents)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      STATUS_STYLES[charge.status] || 'bg-gray-100 text-gray-800'
                    }`}>
                      {STATUS_LABELS[charge.status] || charge.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {formatFixedChargeDate(charge.createdAt)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {formatFixedChargeDate(charge.settledAt)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {writable ? (
                      <button
                        type="button"
                        onClick={() => onWriteOff?.(charge)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                      >
                        Write Off
                      </button>
                    ) : (
                      <span className="text-gray-400">Not available</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const WriteOffModal = ({
  adjustedBy,
  charge,
  confirmed,
  isSubmitting,
  onClose,
  onConfirmChange,
  onReasonChange,
  onSubmit,
  reason,
}) => {
  if (!charge) {
    return null;
  }

  const canSubmit = reason.trim().length > 0 && confirmed && !isSubmitting;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50 px-4">
      <div className="w-full max-w-2xl rounded-md bg-white p-6 shadow-lg">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Write Off Monthly Fixed Charge</h2>
        <p className="text-sm text-gray-700 mb-4">
          You are about to write off this monthly fixed charge. This will set the outstanding amount to
          {` ${centsToRands(0)} `}and mark the charge as WRITTEN_OFF.
        </p>

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700 mb-4">
          <div>
            <dt className="font-medium">Charge UUID</dt>
            <dd className="break-all">{charge.chargeUuid}</dd>
          </div>
          <div>
            <dt className="font-medium">Billing period</dt>
            <dd>{charge.billingPeriod}</dd>
          </div>
          <div>
            <dt className="font-medium">Amount due</dt>
            <dd>{centsToRands(charge.amountDueCents)}</dd>
          </div>
          <div>
            <dt className="font-medium">Already paid</dt>
            <dd>{centsToRands(charge.amountDebitedCents)}</dd>
          </div>
          <div>
            <dt className="font-medium">Outstanding to write off</dt>
            <dd>{centsToRands(charge.amountOutstandingCents)}</dd>
          </div>
          <div>
            <dt className="font-medium">Adjusted by</dt>
            <dd>{adjustedBy}</dd>
          </div>
        </dl>

        <form onSubmit={onSubmit}>
          <div className="mb-4">
            <label htmlFor="writeOffReason" className="block text-sm font-medium text-gray-700 mb-1">
              Reason
            </label>
            <textarea
              id="writeOffReason"
              value={reason}
              onChange={(event) => onReasonChange(event.target.value)}
              disabled={isSubmitting}
              rows={4}
              required
              className="shadow border rounded w-full py-2 px-3 text-gray-700"
              placeholder="Written off from admin portal by Jayson"
            />
          </div>

          <label className="flex items-start gap-2 text-sm text-gray-700 mb-6">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => onConfirmChange(event.target.checked)}
              disabled={isSubmitting}
              className="mt-1"
            />
            <span>I confirm this monthly fixed charge should be written off.</span>
          </label>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={`px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 ${
                !canSubmit ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? 'Writing off...' : 'Confirm Write-Off'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

function MonthlyFixedChargeWriteOffPage() {
  const { user } = useAuth();
  const adjustedBy = getUserEmail(user);
  const canAccessTool = canAccessMonthlyFixedChargeWriteOff(user);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [unitId, setUnitId] = useState('');
  const [accountUuid, setAccountUuid] = useState('');
  const [chargesData, setChargesData] = useState(null);
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingCharges, setLoadingCharges] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [reason, setReason] = useState('Written off from admin portal by Jayson');
  const [confirmed, setConfirmed] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const charges = chargesData?.charges || [];
  const siteOptions = useMemo(() => sites.map((site) => ({
    value: site.uuid || site.siteUuid,
    label: site.name || site.siteName || site.siteId || 'Unnamed site',
    site,
  })).filter((site) => site.value), [sites]);

  useEffect(() => {
    if (!canAccessTool) {
      return;
    }

    const loadSites = async () => {
      setLoadingSites(true);
      setError(null);

      try {
        const siteData = await getActiveSites();
        const sortedSites = Array.isArray(siteData)
          ? [...siteData].sort((a, b) => (a.name || a.siteName || '').localeCompare(b.name || b.siteName || ''))
          : [];

        setSites(sortedSites);
      } catch (loadError) {
        setError('Failed to load sites. Check network connection and permissions.');
        setSites([]);
      } finally {
        setLoadingSites(false);
      }
    };

    loadSites();
  }, [canAccessTool]);

  const resetChargeState = () => {
    setAccountUuid('');
    setChargesData(null);
    setSelectedCharge(null);
    setMessage(null);
  };

  const reloadCharges = async (resolvedAccountUuid) => {
    const data = await getConnectionFeeCharges(resolvedAccountUuid);
    setChargesData(data || { accountUuid: resolvedAccountUuid, charges: [] });
  };

  const handleLoadCharges = async (event) => {
    event.preventDefault();

    if (!canAccessTool) {
      setError('You do not have permission to write off monthly fixed charges.');
      return;
    }

    if (!selectedSite || !unitId.trim()) {
      setError('Select a site and enter a unit ID.');
      return;
    }

    resetChargeState();
    setError(null);
    setLoadingCharges(true);

    try {
      const unit = await getUnitByUnitId(selectedSite.value, unitId.trim());
      const unitUuid = getResolvedUnitUuid(unit);

      if (!unitUuid) {
        setError('Could not find the selected unit.');
        return;
      }

      const account = await getAccountByUnit(
        unit.siteName || selectedSite.label,
        unit.unitId || unitId.trim(),
        selectedSite.value,
        unitUuid
      );
      const resolvedAccountUuid = getResolvedAccountUuid(account);

      if (!resolvedAccountUuid) {
        setError('Could not resolve a billing account for the selected unit.');
        return;
      }

      setAccountUuid(resolvedAccountUuid);
      await reloadCharges(resolvedAccountUuid);
    } catch (loadError) {
      setError(getChargeErrorMessage(loadError));
    } finally {
      setLoadingCharges(false);
    }
  };

  const openWriteOffModal = (charge) => {
    setSelectedCharge(charge);
    setReason('Written off from admin portal by Jayson');
    setConfirmed(false);
    setError(null);
    setMessage(null);
  };

  const handleWriteOffSubmit = async (event) => {
    event.preventDefault();

    if (!canAccessTool) {
      setError('You do not have permission to write off monthly fixed charges.');
      setSelectedCharge(null);
      return;
    }

    if (!selectedCharge?.chargeUuid) {
      setError('Please select a monthly fixed charge to write off.');
      return;
    }

    if (!reason.trim()) {
      setError('Please provide a reason for the write-off.');
      return;
    }

    if (!canWriteOffCharge(selectedCharge)) {
      setError('Only pending or partially paid monthly fixed charges can be written off.');
      setSelectedCharge(null);
      return;
    }

    setSubmitting(true);
    setError(null);
    setMessage(null);

    try {
      await writeOffConnectionFeeCharge({
        chargeUuid: selectedCharge.chargeUuid,
        reason: reason.trim(),
        adjustedBy,
      });

      if (accountUuid) {
        await reloadCharges(accountUuid);
      }

      setSelectedCharge(null);
      setMessage('Monthly fixed charge written off successfully.');
    } catch (writeOffError) {
      setError(getChargeErrorMessage(writeOffError));
    } finally {
      setSubmitting(false);
    }
  };

  if (!canAccessTool) {
    return <MonthlyFixedChargeAccessDenied />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-2 text-gray-800">Monthly Fixed Charge Write-Off</h1>
      <p className="text-gray-600 mb-6">
        Resolve a unit account, review its monthly fixed charge records, and write off a selected outstanding charge.
      </p>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
          {error}
        </div>
      )}

      {message && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4" role="status">
          {message}
        </div>
      )}

      <form onSubmit={handleLoadCharges} className="bg-white shadow-md rounded p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="fixed-charge-site-select" className="block text-sm font-medium text-gray-700 mb-1">
              Site
            </label>
            <Select
              inputId="fixed-charge-site-select"
              options={siteOptions}
              value={selectedSite}
              onChange={(option) => {
                setSelectedSite(option);
                resetChargeState();
              }}
              isLoading={loadingSites}
              isDisabled={loadingSites || loadingCharges}
              placeholder={loadingSites ? 'Loading sites...' : 'Select a site...'}
              classNamePrefix="react-select"
            />
          </div>

          <div>
            <label htmlFor="fixed-charge-unit-id" className="block text-sm font-medium text-gray-700 mb-1">
              Unit ID
            </label>
            <input
              id="fixed-charge-unit-id"
              type="text"
              value={unitId}
              onChange={(event) => {
                setUnitId(event.target.value);
                resetChargeState();
              }}
              disabled={loadingCharges}
              className="shadow border rounded w-full py-2 px-3 text-gray-700"
              placeholder="e.g. 5"
            />
          </div>
        </div>

        <div className="mt-6">
          <button
            type="submit"
            disabled={loadingCharges || loadingSites}
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 ${
              loadingCharges || loadingSites ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {loadingCharges ? 'Loading...' : 'Load Monthly Fixed Charges'}
          </button>
        </div>
      </form>

      {accountUuid && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded mt-6">
          <span className="font-semibold">Account UUID:</span> {accountUuid}
        </div>
      )}

      {chargesData && (
        <MonthlyFixedChargeTable charges={charges} onWriteOff={openWriteOffModal} />
      )}

      {selectedCharge && (
        <WriteOffModal
          adjustedBy={adjustedBy}
          charge={selectedCharge}
          confirmed={confirmed}
          isSubmitting={submitting}
          onClose={() => setSelectedCharge(null)}
          onConfirmChange={setConfirmed}
          onReasonChange={setReason}
          onSubmit={handleWriteOffSubmit}
          reason={reason}
        />
      )}
    </div>
  );
}

MonthlyFixedChargeTable.propTypes = {
  charges: PropTypes.arrayOf(
    PropTypes.shape({
      chargeUuid: PropTypes.string,
      billingPeriod: PropTypes.string,
      amountDueCents: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      amountDebitedCents: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      amountOutstandingCents: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      status: PropTypes.string,
      createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      settledAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  onWriteOff: PropTypes.func,
};

WriteOffModal.propTypes = {
  adjustedBy: PropTypes.string.isRequired,
  charge: PropTypes.shape({
    chargeUuid: PropTypes.string,
    billingPeriod: PropTypes.string,
    amountDueCents: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    amountDebitedCents: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    amountOutstandingCents: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
  }),
  confirmed: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirmChange: PropTypes.func.isRequired,
  onReasonChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  reason: PropTypes.string.isRequired,
};

export default MonthlyFixedChargeWriteOffPage;
